import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtUsd } from "@/lib/nexus";
import LiveFeed from "@/components/LiveFeed";
import AlertsPanel from "@/components/AlertsPanel";
import { Users, Wallet, Activity, ShieldAlert, Cpu, Link2, Network, Globe2, ArrowRight } from "lucide-react";

function KPI({ icon: Icon, label, value, sub, accent = "text-nexus-cyan", testid }) {
  return (
    <div data-testid={testid} className="panel p-3 brackets">
      <span className="b1" /><span className="b2" />
      <div className="flex items-center justify-between">
        <div className="panel-title">{label}</div>
        <Icon size={14} className={accent} strokeWidth={1.6} />
      </div>
      <div className={`metric-hero mt-2 ${accent}`}>{value}</div>
      <div className="text-[10px] font-mono text-nexus-text-3 uppercase tracking-widest mt-1">{sub}</div>
    </div>
  );
}

function InvestigationCard({ to, icon: Icon, title, subtitle, accent = "#00F0FF", testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="panel p-4 brackets group hover:bg-nexus-panel-2/50 transition"
    >
      <span className="b1" /><span className="b2" />
      <div className="flex items-start justify-between">
        <div>
          <div className="panel-title flex items-center gap-2">
            <Icon size={13} style={{ color: accent }} />
            <span>{title}</span>
          </div>
          <div className="text-[13px] text-nexus-text-2 mt-2 max-w-[24rem]">{subtitle}</div>
        </div>
        <ArrowRight
          size={16}
          className="text-nexus-text-3 group-hover:text-nexus-cyan group-hover:translate-x-1 transition"
        />
      </div>
    </Link>
  );
}

export default function CommandCenter() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/stats/overview").then((r) => setStats(r.data));
  }, []);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="panel-title">COMMAND CENTER // AGENTIC PAYMENTS INTEL</div>
          <h1 className="font-rajdhani font-bold text-2xl tracking-tight mt-1">
            What agents pay each other — <span className="text-nexus-cyan">observed and correlated.</span>
          </h1>
        </div>
        <div className="text-[10px] font-mono text-nexus-text-3 uppercase tracking-widest">
          data // synthetic + open-source intel · last sync {stats ? new Date(stats.updated_at).toISOString().slice(11, 19) : "…"}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPI testid="kpi-agents" icon={Users} label="Agents Tracked" value={stats?.agents_total ?? "—"} sub={`${stats?.frameworks_tracked ?? 0} frameworks`} />
        <KPI testid="kpi-tvl" icon={Wallet} label="Agent TVL" value={fmtUsd(stats?.tvl_usd)} sub="on-chain balance" />
        <KPI testid="kpi-volume" icon={Activity} label="A2A Volume 24H" value={fmtUsd(stats?.a2a_volume_24h_usd)} sub={`${stats?.transactions_total ?? 0} txns tracked`} accent="text-nexus-violet" />
        <KPI testid="kpi-critical" icon={ShieldAlert} label="Critical Agents" value={stats?.agents_critical ?? "—"} sub={`${stats?.active_alerts ?? 0} live alerts`} accent="text-nexus-rose" />
        <KPI testid="kpi-watch" icon={Cpu} label="Watchlist" value={stats?.agents_watch ?? "—"} sub="under investigation" accent="text-nexus-amber" />
        <KPI testid="kpi-chains" icon={Link2} label="Chains Live" value={stats?.chains_tracked ?? "—"} sub="etherscan · solana · base" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <InvestigationCard
          to="/graph"
          icon={Network}
          title="OPEN GRAPH WORKSPACE"
          subtitle="Investigate A2A flows with a clean, clustered view. Use natural-language queries (Claude Sonnet 4.5) to slice the graph — 'show me drain patterns from LangChain agents on Ethereum'. Drill into any node without leaving the map."
          testid="cc-open-graph"
        />
        <InvestigationCard
          to="/map"
          icon={Globe2}
          title="OPEN GLOBAL MAP"
          subtitle="See where agents physically live — real datacenter regions plotted on a world map. Same NL query bar zooms into geographies of interest. Click any dot for the full agent schema."
          accent="#9D4EDD"
          testid="cc-open-map"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <AlertsPanel compact />
        </div>
        <div className="min-h-[520px] max-h-[520px]">
          <LiveFeed limit={14} />
        </div>
      </div>
    </div>
  );
}
